/* eslint-disable */
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import cn from 'clsx';
import Card from 'components/card';
import Pagination from 'components/Pagination/Pagination';
import { useMemo, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';
import { formatDate } from 'utils/date';
import { statusToText } from 'utils/status-to-text';
import { txsService } from 'views/services/txsService';
import { IDepositExtended } from 'views/types/typeTransaction';
import { TransactionDialog } from '../TransactionDialog';
import {
  EOrderCurrency,
  EOrderStatus,
  EOrderType,
  ORDER_STATUS_OPTIONS,
  SHOW_OPERATION_DIRECTION_TYPES,
} from 'views/types/transactions';
import { formatUnits } from 'viem';
import { formatNumber } from 'utils/formatNumber';
import dayjs from 'dayjs';
import { EDatePresets } from '../../../../types/date-range-preset.enum';
import DatePicker from 'react-datepicker';
import {
  PermissionGate,
  ReadPermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

export const SortIcon = ({ direction }: { direction?: 'asc' | 'desc' }) => {
  const Icon =
    direction === 'asc' ? FaSortUp : direction === 'desc' ? FaSortDown : FaSort;

  return (
    <Icon
      className={cn(
        'text-[10px] transition-colors',
        direction
          ? 'text-blue-500 dark:text-blue-400'
          : 'text-gray-400 dark:text-gray-600'
      )}
    />
  );
};

const columnHelper = createColumnHelper<IDepositExtended>();

const getColumns = (txType: string) => {
  const baseColumns: ColumnDef<IDepositExtended, any>[] = [
    columnHelper.accessor('id', {
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Id
            <SortIcon
              direction={
                sortDirection === 'asc'
                  ? 'desc'
                  : sortDirection === 'desc'
                    ? 'asc'
                    : undefined
              }
            />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {info.getValue()}
        </p>
      ),
      sortDescFirst: true,
    }),
    columnHelper.accessor('user.coins_fly_id', {
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Client ID
            <SortIcon
              direction={
                sortDirection === 'asc'
                  ? 'desc'
                  : sortDirection === 'desc'
                    ? 'asc'
                    : undefined
              }
            />
          </p>
        );
      },
      cell: info => {
        const row = info.row.original;
        if (row.type === EOrderType.INTERNAL) {
          let clientId = '---';
          const amount = Number(row.amountIn);
          if (amount > 0 && row.receiver) {
            clientId = row.receiver.coins_fly_id || '---';
          } else if (amount < 0 && row.user) {
            clientId = row.user.coins_fly_id || '---';
          }
          return (
            <p className="text-sm font-bold text-navy-700 dark:text-white truncate capitalize">
              {clientId}
            </p>
          );
        }
        let clientId =
          row.user?.coins_fly_id || row.card?.user?.coins_fly_id || '---';
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate capitalize">
            {clientId}
          </p>
        );
      },
      sortDescFirst: true,
    }),
    columnHelper.accessor('type', {
      sortDescFirst: true,
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Type
            <SortIcon
              direction={
                sortDirection === 'asc'
                  ? 'desc'
                  : sortDirection === 'desc'
                    ? 'asc'
                    : undefined
              }
            />
          </p>
        );
      },

      cell: info => {
        let typeValue = info.getValue()?.replaceAll('-', ' ');

        if (typeValue && typeValue === 'internal') {
          const isPositive = +info.row.original.amountIn > 0;
          typeValue = isPositive
            ? typeValue + ' deposit'
            : typeValue + ' withdraw';
        }

        const displayType = txType?.replaceAll('-', ' ');

        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate capitalize">
            {typeValue ?? displayType}
          </p>
        );
      },
    }),
    columnHelper.accessor(row => row.amount || row.amountIn, {
      id: 'amount',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Amount
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => {
        const amountRaw = info.getValue();
        const currency =
          info.row.original.currency || info.row.original.currencyIn;
        const decimals = currency?.decimals || 0;

        try {
          const formattedAmount = formatUnits(
            BigInt(Math.abs(Math.trunc(+amountRaw))),
            decimals
          );
          return (
            <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
              {formatNumber(formattedAmount)}
            </p>
          );
        } catch {
          return (
            <p className="text-sm font-bold text-red-500 truncate">
              Invalid amount
            </p>
          );
        }
      },
      sortDescFirst: false,
    }),
    columnHelper.display({
      id: 'credit-debit',
      header: () => (
        <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
          Credit/Debit
        </p>
      ),
      cell: info => {
        const typeValue = info.row.original.type as EOrderType;

        if (SHOW_OPERATION_DIRECTION_TYPES.includes(typeValue)) {
          const isPositive = +info.row.original.amountIn > 0;
          const isCredit =
            [
              EOrderType.DEPOSIT,
              EOrderType.CARD_TOP_UP,
              EOrderType.REFERRAL_TRANSACTION,
            ].includes(typeValue) ||
            ([EOrderType.CARD_TRANSACTION, EOrderType.INTERNAL].includes(
              typeValue
            ) &&
              isPositive);

          if (isCredit) {
            return (
              <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 dark:bg-green-900 dark:text-green-200">
                credit
              </span>
            );
          } else {
            return (
              <span className="inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 dark:bg-red-900 dark:text-red-200">
                debit
              </span>
            );
          }
        } else {
          return <></>;
        }
      },
      sortDescFirst: false,
    }),
    columnHelper.accessor('createdAt', {
      sortDescFirst: true,
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Date
            <SortIcon
              direction={
                sortDirection === 'asc'
                  ? 'desc'
                  : sortDirection === 'desc'
                    ? 'asc'
                    : undefined
              }
            />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {formatDate(info.getValue(), 'DD.MM.YYYY HH:mm')}
        </p>
      ),
    }),
    ...(txType !== 'internal' &&
    txType !== 'swaps' &&
    txType !== 'card-transaction' &&
    txType !== 'referral-transaction'
      ? [
          columnHelper.accessor('amlLog', {
            header: ({ column }) => {
              const sortDirection = column.getIsSorted();

              return (
                <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
                  AML
                  {txType === 'allTxs' && (
                    <SortIcon direction={sortDirection as 'asc' | 'desc'} />
                  )}
                </p>
              );
            },
            cell: info => {
              const value = info.getValue() as any | null;
              const riskScoreRaw =
                value?.responseData?.result?.data?.risk_score ??
                value?.responseData?.risk_score;

              const riskScore =
                riskScoreRaw !== undefined && riskScoreRaw !== null
                  ? `${Math.round(Number(riskScoreRaw) * 100)}%`
                  : '---';

              return (
                <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
                  {riskScore}
                </p>
              );
            },
            sortDescFirst: false,
          }),
        ]
      : []),
    columnHelper.accessor('status', {
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Status
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => {
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate capitalize">
            {statusToText(info.getValue(), info.row.original.type)}
          </p>
        );
      },
      sortDescFirst: false,
    }),
  ];

  if (txType === 'swaps') {
    baseColumns.push(
      columnHelper.accessor('currencyIn', {
        id: 'currencyInSymbol',
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();

          return (
            <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
              Currency In
              <SortIcon direction={sortDirection as 'asc' | 'desc'} />
            </p>
          );
        },
        cell: info => (
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
            {info.getValue()?.symbol ?? '---'}
          </p>
        ),
        sortDescFirst: false,
      }),

      columnHelper.accessor('currencyOut', {
        id: 'currencyOutSymbol',
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();

          return (
            <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
              Currency Out
              <SortIcon direction={sortDirection as 'asc' | 'desc'} />
            </p>
          );
        },
        cell: info => (
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
            {info.getValue()?.symbol ?? '---'}
          </p>
        ),
        sortDescFirst: false,
      })
    );
  } else {
    baseColumns.push(
      columnHelper.accessor('currency', {
        id: 'currencySymbol',
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();

          return (
            <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
              Currency
              <SortIcon
                direction={
                  sortDirection === 'asc'
                    ? 'desc'
                    : sortDirection === 'desc'
                      ? 'asc'
                      : undefined
                }
              />
            </p>
          );
        },
        cell: info => {
          const row = info.row.original;

          const symbol =
            typeof row.currency === 'string'
              ? row.currency
              : typeof row.currencyIn === 'string'
                ? row.currencyIn
                : typeof row.currencyOut === 'string'
                  ? row.currencyOut
                  : (row.currency?.symbol ??
                    row.currencyIn?.symbol ??
                    row.currencyOut?.symbol ??
                    '---');

          return (
            <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
              {symbol}
            </p>
          );
        },
      })
    );
  }
  return baseColumns;
};

interface Paginated<T> {
  data: T[];
  meta: {
    totalItems: number;
    totalPages: number;
  };
}

export const TransactionsTable = () => {
  const [sorting, setSorting] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [txType, setTxType] = useState<EOrderType>(EOrderType.ALL);
  const { canUpdate, canRead } = usePermissions();

  const [currencyFilter, setCurrencyFilter] = useState<EOrderCurrency>(
    EOrderCurrency.All
  );
  const [statusFilter, setStatusFilter] = useState<EOrderStatus>(
    EOrderStatus.ALL
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<IDepositExtended | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [preset, setPreset] = useState<EDatePresets>(EDatePresets.ALL);
  const [[startDate, endDate], setDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const applyPreset = (p: EDatePresets) => {
    setPreset(p);
    let from: Date | null = null;
    let to: Date | null = null;
    switch (p) {
      case EDatePresets.DAY:
        from = dayjs().startOf('day').toDate();
        to = dayjs().endOf('day').toDate();
        break;
      case EDatePresets.WEEK:
        from = dayjs().startOf('week').toDate();
        to = dayjs().endOf('week').toDate();
        break;
      case EDatePresets.MONTH:
        from = dayjs().startOf('month').toDate();
        to = dayjs().endOf('month').toDate();
        break;
      case EDatePresets.ALL:
      default:
        break;
    }
    setDateRange([from, to]);
    setPage(0);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  const [searchDebounce] = useDebounceValue(search, 1000);

  const sortingData = useMemo(() => {
    if (!!sorting.length) {
      const [sort] = sorting;

      return {
        sortBy: sort.id,
        sortOrder: sort.desc ? 'ASC' : 'DESC',
      };
    } else {
      return {};
    }
  }, [sorting]);

  const { data, isLoading, mutate } = useSWR<Paginated<IDepositExtended>>(
    [
      `/api/orders/all-transactions`,
      txType,
      currencyFilter,
      statusFilter,
      page,
      itemsPerPage,
      searchDebounce,
      sortingData.sortBy,
      sortingData.sortOrder,
      preset,
      startDate?.toISOString(),
      endDate?.toISOString(),
    ],
    () => {
      const params: any = {
        page: page + 1,
        limit: itemsPerPage,
        search: searchDebounce,
        txType: txType.replaceAll('blocked-', ''),
        currency:
          currencyFilter !== EOrderCurrency.All
            ? (currencyFilter as string)
            : undefined,
        status:
          statusFilter !== EOrderStatus.ALL
            ? (statusFilter as string)
            : undefined,
        ...sortingData,
      };

      if (startDate) {
        params.from = dayjs(startDate).startOf('day').toISOString();
      }
      if (endDate) {
        params.to = dayjs(endDate).endOf('day').toISOString();
      }

      return txsService.getAllTransactions(params);
    },
    {
      refreshInterval: 10000,
    }
  );

  const columns = useMemo(() => getColumns(txType), [txType]);

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
    manualSorting: true,
  });

  return (
    <Card
      extra={
        'relative flex flex-col rounded-[15px] border-[1px] border-gray-200 bg-white bg-clip-border shadow-md shadow-[#F3F3F3] dark:border-[#ffffff33] dark:!bg-navy-800 dark:text-white dark:shadow-none w-full !h-[75vh] sm:overflow-auto mt-9 px-6 pb-6'
      }
    >
      <header className="relative flex flex-col md:flex-row md:items-center gap-4 pt-4">
        <ReadPermissionGate resource={PERMISSION_RESOURCES.FILTER_TRANSACTIONS}>
          <select
            value={txType}
            onChange={e => {
              setTxType(e.target.value as any);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
              !canUpdate(PERMISSION_RESOURCES.FILTER_TRANSACTIONS)
            }
            className="w-[200px] rounded-lg border p-2 bg-white dark:bg-navy-900 text-navy-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value={EOrderType.ALL}>All Transactions</option>
            <option value={EOrderType.DEPOSIT}>Deposits</option>
            <option value={EOrderType.WITHDRAW}>Withdrawals</option>
            <option value={EOrderType.SWAP}>Swaps</option>
            <option value={EOrderType.INTERNAL}>Internal</option>
            <option value={EOrderType.CARD_ISSUE}>Card Issue</option>
            <option value={EOrderType.CARD_TOP_UP}>Card Top Up</option>
            <option value={EOrderType.CARD_WITHDRAW}>Card Withdraw</option>
            <option value={EOrderType.CARD_TRANSACTION}>
              Card Transactions
            </option>
            <option value={EOrderType.REFERRAL_TRANSACTION}>
              Referral Transactions
            </option>
            <option value={EOrderType.AML_WALLET_CHECK}>
              AML Wallet Check
            </option>
            <option value={EOrderType.AML_TX_CHECK}>AML TX Check</option>
          </select>

          <select
            value={currencyFilter}
            onChange={e => {
              setCurrencyFilter(e.target.value as EOrderCurrency);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
              !canUpdate(PERMISSION_RESOURCES.FILTER_TRANSACTIONS)
            }
            className="rounded-lg border p-2 bg-white dark:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value={EOrderCurrency.All}>All Currencies</option>
            <option value={EOrderCurrency.USDT}>USDT</option>
            <option value={EOrderCurrency.USDC}>USDC</option>
            <option value={EOrderCurrency.TRX}>TRX</option>
            <option value={EOrderCurrency.SOL}>SOL</option>
            <option value={EOrderCurrency.BTC}>BTC</option>
            <option value={EOrderCurrency.ETH}>ETH</option>
          </select>

          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as EOrderStatus);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
              !canUpdate(PERMISSION_RESOURCES.FILTER_TRANSACTIONS)
            }
            className="rounded-lg border p-2 bg-white dark:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value={EOrderStatus.ALL}>All statuses</option>
            {ORDER_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={dates => {
              setPreset(EDatePresets.ALL);
              const [rawFrom, rawTo] = dates as [Date, Date];
              const from = rawFrom
                ? dayjs(rawFrom).startOf('day').toDate()
                : null;
              const to = rawTo ? dayjs(rawTo).endOf('day').toDate() : null;
              setDateRange([from, to]);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
              !canUpdate(PERMISSION_RESOURCES.FILTER_TRANSACTIONS)
            }
            isClearable
            placeholderText="Custom range…"
            className="rounded border px-2 py-1 bg-white dark:bg-navy-900 text-navy-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            popperClassName="react-datepicker-popper"
          >
            <div className="flex gap-2 p-2 border-t bg-white">
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.DAY)}
              >
                Today
              </button>
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.WEEK)}
              >
                This Week
              </button>
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.MONTH)}
              >
                This Month
              </button>
            </div>
          </DatePicker>
        </ReadPermissionGate>

        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.SEARCH_BUTTON_TRANSACTIONS}
        >
          <input
            type="text"
            placeholder="Search by Transaction ID, Client ID, Email, TG Name..."
            className="block min-h-7 ml-auto w-full grow rounded-full bg-lightPrimary px-4 text-base font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:h-full sm:w-fit max-w-[25rem] disabled:opacity-50 disabled:cursor-not-allowed"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS_PAGE) ||
              !canUpdate(PERMISSION_RESOURCES.SEARCH_BUTTON_TRANSACTIONS)
            }
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            autoCapitalize="none"
          />
        </ReadPermissionGate>
      </header>
      {isLoading ? (
        <tr>
          <td className="absolute left-1/2 -translate-x-1/2" colSpan={8}>
            <p className="flex justify-center py-10">
              <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
            </p>
          </td>
        </tr>
      ) : !data || data.data.length === 0 ? (
        <tr>
          <td className="absolute left-1/2 -translate-x-1/2" colSpan={8}>
            <p className="py-10 text-center font-bold uppercase">No data</p>
          </td>
        </tr>
      ) : (
        <>
          <div className="mt-8 grow overflow-x-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={
                          ['type', 'amlLog'].includes(header.id) &&
                          txType !== 'allTxs'
                            ? () => {}
                            : header.column.getToggleSortingHandler()
                        }
                        className="bg-white pb-2 pr-4 pt-4 text-start dark:bg-navy-800 cursor-pointer"
                      >
                        <div className="items-center justify-between text-xs text-gray-200">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      if (
                        canRead(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE)
                      ) {
                        setSelectedTransaction(row.original);
                        setIsDialogOpen(true);
                      }
                    }}
                    className={`transition-colors duration-200 [&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900 ${
                      canRead(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE)
                        ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                  >
                    {row.getVisibleCells().map(cell => {
                      return (
                        <td
                          key={cell.id}
                          className="min-w-[100px] border-white/0 py-2 pr-4 first:rounded-l-lg last:rounded-r-lg last:pr-0"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {selectedTransaction && (
              <TransactionDialog
                transaction={selectedTransaction}
                refetchDeposits={mutate}
                isOpen={isDialogOpen}
                onClose={() => {
                  setIsDialogOpen(false);
                  setSelectedTransaction(null);
                }}
                isInternal={
                  selectedTransaction.type === EOrderType.INTERNAL ||
                  txType === EOrderType.INTERNAL
                }
                isWithdraw={txType === EOrderType.WITHDRAW}
                isSwap={txType === EOrderType.SWAP}
                isCardTransaction={txType === EOrderType.CARD_TRANSACTION}
                isReferralTransaction={
                  txType === EOrderType.REFERRAL_TRANSACTION ||
                  selectedTransaction.type === EOrderType.REFERRAL_TRANSACTION
                }
              />
            )}
          </div>
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages}
            totalItems={data?.meta?.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalName="Total Transactions"
          />
        </>
      )}
    </Card>
  );
};
